import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { selectAccessToken } from "../store/auth";
import * as mediaContentApi from "../api/mediaContent";
import { CyberpassButton } from "../Input";
import { useTranslation } from "../i18n";

const FileManager = () => {
  const { t } = useTranslation();
  const accessToken = useSelector(selectAccessToken);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [newName, setNewName] = useState("");

  const fetchFiles = async () => {
    try {
      const fileNames =
        await mediaContentApi.fetchAllMediaFileNames(accessToken);
      setFiles(fileNames.sort((a, b) => a.localeCompare(b)));
    } catch (error) {
      toast.error(t("fileManager.loadError"));
      console.error(error);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [accessToken]);

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      await mediaContentApi.uploadFile(accessToken, file);
      toast.success(t("fileManager.uploadSuccess"));
      await fetchFiles();
    } catch (error) {
      toast.error(error.message || t("fileManager.uploadError"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileOpen = (fileName) => {
    const HOST = import.meta.env.VITE_API_HOST;
    const url = `${HOST}/api/v1/media-content/file/${fileName}`;
    window.open(url, "_blank");
  };

  const handleContextMenu = (event, fileName) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      fileName,
    });
  };

  const handleDelete = async (fileName) => {
    if (!window.confirm(t("confirm.deleteFile", { fileName }))) return;

    try {
      await mediaContentApi.deleteFile(accessToken, fileName);
      toast.success(t("fileManager.deleteSuccess"));
      await fetchFiles();
    } catch (error) {
      toast.error(error.message || t("fileManager.deleteError"));
    }
    setContextMenu(null);
  };

  const handleRename = (fileName) => {
    setRenaming(fileName);
    setNewName(fileName);
    setContextMenu(null);
  };

  const confirmRename = async () => {
    if (!renaming || !newName) return;

    try {
      await mediaContentApi.renameFile(accessToken, renaming, newName);
      toast.success(t("fileManager.renameSuccess"));
      await fetchFiles();
      setRenaming(null);
      setNewName("");
    } catch (error) {
      toast.error(error.message || t("fileManager.renameError"));
    }
  };

  const cancelRename = () => {
    setRenaming(null);
    setNewName("");
  };

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl">Управление файлами</h1>
          <CyberpassButton onClick={() => navigate(-1)}>Назад</CyberpassButton>
        </div>

        <div className="bg-c-gray-600 rounded-lg shadow p-6 mb-6">
          <h2 className="text-2xl mb-4">Загрузка файлов</h2>
          <div className="flex items-center gap-4">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              disabled={uploading}
              className="flex-1 text-sm text-c-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-white hover:file:bg-accent-hover cursor-pointer"
              accept="image/*,video/*"
            />
            {uploading && <span className="text-c-gray-400">Загрузка...</span>}
          </div>
        </div>

        <div className="bg-c-gray-600 rounded-lg shadow p-6">
          <h2 className="text-2xl mb-4">Файлы</h2>
          {files.length === 0 ? (
            <p className="text-c-gray-400">Нет загруженных файлов</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {files.map((fileName) => (
                <div
                  key={fileName}
                  className="bg-c-gray-500 border border-c-gray-400/20 rounded-lg p-4 hover:bg-c-gray-500/80 transition-colors"
                  onContextMenu={(e) => handleContextMenu(e, fileName)}
                >
                  {renaming === fileName ? (
                    <div
                      className="space-y-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        className="w-full px-2 py-1 bg-c-gray-700 border border-c-gray-400/30 rounded text-gray-100 focus:outline-none focus:border-accent"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={confirmRename}
                          className="px-3 py-1 bg-c-green-500 hover:bg-c-green-600 text-white rounded text-sm transition-colors"
                        >
                          Сохранить
                        </button>
                        <button
                          onClick={cancelRename}
                          className="px-3 py-1 bg-c-gray-400 hover:bg-c-gray-400/80 text-white rounded text-sm transition-colors"
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-6 h-6 text-c-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-sm truncate flex-1">
                        {fileName}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {contextMenu && (
          <div
            className="fixed bg-c-gray-500 border border-c-gray-400/30 rounded-lg shadow-lg py-2 z-50"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                handleFileOpen(contextMenu.fileName);
                setContextMenu(null);
              }}
              className="w-full px-4 py-2 text-left hover:bg-c-gray-600 text-sm transition-colors"
            >
              Открыть
            </button>
            <button
              onClick={() => handleRename(contextMenu.fileName)}
              className="w-full px-4 py-2 text-left hover:bg-c-gray-600 text-sm transition-colors"
            >
              Переименовать
            </button>
            <button
              onClick={() => handleDelete(contextMenu.fileName)}
              className="w-full px-4 py-2 text-left hover:bg-c-gray-600 text-c-red-500 text-sm transition-colors"
            >
              Удалить
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileManager;
